import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Typography,
  Divider,
  styled,
} from '@mui/material'
import React, { FC, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { FilterButton } from '../filter/FilterButton'
import CalendarIcon from '@/assets/icons/calendar.svg'
import BottomArrowIcon from '@/assets/icons/bottom-arrow.svg'
import { AuditTrailTable } from '../table/AuditTrailTable'
import { AuditTrailData } from '@/utilities/types'
import { AuditTableProps } from '@/utilities/types/table-props'
import { useForm } from '@/utilities'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { GenericModal } from './GenericModal'
import { CustomDatePicker } from '../date/CustomDatePicker'
import { Dayjs } from 'dayjs'
import { useMaintenanceTicketsContext } from '@/context/MaintenanceTicketsContext'
import { FilterMenu } from '../menu/FilterMenu'

type DateFilters = {
  startCreatedDate?: string
  endCreatedDate?: string
  startModifiedDate?: string
  endModifiedDate?: string
}
interface AuditTrailModalProps extends AuditTableProps {
  title: string
  open: boolean
  onClose: (type: string) => void
  dateFilters: DateFilters
}

interface ActionButtonProps extends ButtonProps {
  bgcolor: string
}

const ActionButton = styled(Button)<ActionButtonProps>(({ bgcolor }) => ({
  backgroundColor: bgcolor,
}))

const options = [
  { label: 'Attachments', value: 'attachment(s)' },
  { label: 'Branch Id', value: 'branchId' },
  { label: 'Cost Sharing', value: 'costSharing' },
  { label: 'Description', value: 'description' },
  { label: 'Method of Repair', value: 'methodOfRepair' },
  { label: 'Purpose', value: 'purpose' },
  { label: 'Status', value: 'status' },
  { label: 'Type of Collateral', value: 'typeOfCollateral' },
  { label: 'Type of Concern', value: 'typeOfConcern' },
  { label: 'Value', value: 'value' },
]

export const AuditTrailModal: FC<AuditTrailModalProps> = ({
  open,
  onClose,
  title,
  data,
  ...props
}) => {
  const { filterAuditTrail, filterDataAuditTrail } =
    useMaintenanceTicketsContext()

  const {
    showModal: showFilter,
    handleCloseModal: handleCloseDateFilter,
    handleAddData: handleShowDateFilter,
  } = useForm()

  const [filter, setFilter] = useState({
    startCreatedDate: '',
    endCreatedDate: '',
  })

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const showMenu = Boolean(anchorEl)

  const handleChangeDate = (field: string, newValue: Dayjs | null) => {
    const formattedDate = newValue ? newValue.format('YYYY-MM-DD') : null

    setFilter((prev) => ({
      ...prev,
      [field]: formattedDate,
    }))
  }

  const handleApplyFilter = () => {
    filterAuditTrail(filter)
    handleCloseDateFilter()
  }

  const handleResetFilter = () => {
    setFilter({
      startCreatedDate: '',
      endCreatedDate: '',
    })
  }

  const handleShowMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleSelect = (value: string) => {
    const selectedValue = value === selectedOption ? '' : value
    setSelectedOption(selectedValue)
    filterDataAuditTrail(selectedValue)
  }

  return (
    <React.Fragment>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ px: '2rem', pt: '1.5rem' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontFamily: 'Gabarito',
                  fontWeight: 500,
                  fontSize: '22px',
                  color: '#505470',
                }}
              >
                {title ?? 'Default Title'}
              </Typography>
              <IconButton
                size="small"
                aria-label="close"
                onClick={onClose}
                sx={{
                  color: '#505470',
                  width: 20,
                  height: 20,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ my: 2, borderWidth: 2, color: '#B1B8BF' }} />
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 0,
            px: '2rem',
            pb: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            overflowY: 'auto',
          }}
        >
          <Grid2
            container
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              gap: {
                xs: 1.5,
                sm: 0,
              },
            }}
          >
            <FilterButton
              onClick={handleShowDateFilter}
              icon={CalendarIcon}
              height={16}
              width={16}
              fontSize="12px"
            />

            <FilterButton
              onClick={handleShowMenu}
              icon={BottomArrowIcon}
              height={16}
              width={16}
              fontSize="12px"
              title="Data"
            />

            <FilterMenu
              anchorEl={anchorEl}
              selectedOption={selectedOption}
              showMenu={showMenu}
              options={options}
              handleClose={handleCloseMenu}
              handleSelect={handleSelect}
            />
          </Grid2>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <GenericModal
              open={showFilter}
              onClose={handleApplyFilter}
              title="Date Filter"
              handleApply={handleApplyFilter}
              handleReset={handleResetFilter}
            >
              <Box
                display="flex"
                gap={1}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <CustomDatePicker
                  label="Start Created Date"
                  value={filter.startCreatedDate ?? ''}
                  onChange={handleChangeDate}
                  field="startCreatedDate"
                />
                <CustomDatePicker
                  label="End Created Date"
                  value={filter.endCreatedDate ?? ''}
                  onChange={handleChangeDate}
                  field="endCreatedDate"
                />
              </Box>
            </GenericModal>
          </LocalizationProvider>
          <AuditTrailTable data={data as AuditTrailData[]} {...props} />
        </DialogContent>
        <DialogActions
          sx={{ px: '2rem', pb: '1rem', justifyContent: 'flex-end' }}
        >
          <ActionButton
            variant="contained"
            bgcolor="#495AFF"
            onClick={() => {
              onClose('DEFAULT')
            }}
          >
            Close
          </ActionButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}
